import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SignaturePad } from "@/components/phones/SignaturePad";
import { PlateCombobox } from "@/components/PlateCombobox";
import { api, errMsg } from "@/lib/api";

const MODES = {
  fixed: { title: "Assegnatario fisso", desc: "Il telefono resta assegnato a questa persona finché non lo liberi.", ok: "Assegna" },
  daily: { title: "Consegna giornaliera", desc: "Registra il ritiro: a fine giornata servirà la firma di riconsegna.", ok: "Registra ritiro" },
  return: { title: "Riconsegna", desc: "Firma di chi riconsegna il telefono.", ok: "Registra riconsegna" },
};

export const SignDialog = ({ action, onClose, onDone }) => {
  const [name, setName] = useState(action?.phone?.fixed_assignee || "");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [free, setFree] = useState(false);
  const sid = action?.phone?.station_id;
  useEffect(() => {
    if (!sid || action.mode === "return") return;
    api.get("/drivers", { params: { station_id: sid } }).then((r) => {
      const list = r.data.filter((d) => d.active).map((d) => d.name);
      setDrivers(list);
      if (!list.length || (name && !list.includes(name))) setFree(true);
    }).catch(() => setFree(true));
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!action) return null;
  const { mode, phone } = action;
  const m = MODES[mode];

  const save = async () => {
    setBusy(true);
    try {
      if (mode === "return") await api.post(`/phones/assignments/${phone.daily.id}/return`, { signature: sig });
      else await api.post(`/phones/${phone.id}/${mode === "fixed" ? "fixed" : "checkout"}`, { assignee: name, signature: sig });
      toast.success(mode === "return" ? "Riconsegna registrata" : mode === "fixed" ? "Assegnatario fisso salvato" : "Ritiro registrato");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="phone-sign-dialog">
        <DialogHeader>
          <DialogTitle>{m.title} · {phone.brand} {phone.model}</DialogTitle>
          <DialogDescription>IMEI {phone.imei}{phone.phone_number ? ` · ${phone.phone_number}` : ""}. {m.desc}</DialogDescription>
        </DialogHeader>
        {mode === "return"
          ? <p className="text-sm">Assegnatario: <b data-testid="phone-sign-assignee">{phone.daily.assignee}</b></p>
          : <div className="space-y-1.5">
            <Label>{free ? "Nome e cognome assegnatario" : "Driver della stazione"}</Label>
            {free
              ? <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="phone-assignee-input" />
              : <PlateCombobox value={name} onChange={setName} testId="phone-assignee-driver-select" placeholder="Seleziona driver" searchPlaceholder="Cerca driver..."
                  options={drivers.map((n) => ({ value: n, label: n }))} />}
            {drivers.length > 0 && <button type="button" className="text-xs font-medium text-blue-600 hover:underline" onClick={() => { setFree(!free); setName(""); }} data-testid="phone-assignee-mode-toggle">
              {free ? "Scegli dall'elenco driver" : "Non è un driver? Scrivi il nome"}</button>}
          </div>}
        <SignaturePad onChange={setSig} testId="phone-signature-pad" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy || !sig || (mode !== "return" && name.trim().length < 2)} data-testid="phone-sign-save-button">{busy ? "Salvataggio..." : m.ok}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
